/**
 * Parses positive currency input without silently converting malformed values to zero.
 * Turkish notation is preferred: 1.234,56 and 1.234.567.
 * International notation (1,234.56) is also accepted.
 */
export function parseCurrencyAmount(value: string): number | null {
  let input = value.trim().replace(/\s+/g, "");
  input = input.replace(/^(?:₺|TRY|TL)/i, "").replace(/(?:₺|TRY|TL)$/i, "");
  if (!input || !/^\d[\d.,]*$/.test(input)) return null;

  const commaCount = (input.match(/,/g) ?? []).length;
  const dotCount = (input.match(/\./g) ?? []).length;
  let normalized = input;

  if (commaCount > 0 && dotCount > 0) {
    const decimalSeparator = input.lastIndexOf(",") > input.lastIndexOf(".") ? "," : ".";
    const groupingSeparator = decimalSeparator === "," ? "." : ",";
    const decimalIndex = input.lastIndexOf(decimalSeparator);
    const integerPart = input.slice(0, decimalIndex);
    const fractionPart = input.slice(decimalIndex + 1);
    const groupingPattern = groupingSeparator === "."
      ? /^\d{1,3}(?:\.\d{3})+$/
      : /^\d{1,3}(?:,\d{3})+$/;
    if (!/^\d{1,2}$/.test(fractionPart)) return null;
    if (integerPart.includes(groupingSeparator) && !groupingPattern.test(integerPart)) return null;
    if (integerPart.includes(decimalSeparator)) return null;
    normalized = integerPart.split(groupingSeparator).join("") + "." + fractionPart;
  } else if (commaCount + dotCount > 0) {
    const separator = commaCount > 0 ? "," : ".";
    const count = commaCount + dotCount;
    const parts = input.split(separator);
    if (parts.some((part) => part.length === 0 || !/^\d+$/.test(part))) return null;

    if (count === 1) {
      const [whole, fraction] = parts;
      if (fraction.length <= 2) {
        normalized = whole + "." + fraction;
      } else if (fraction.length === 3 && whole.length <= 3) {
        normalized = whole + fraction;
      } else {
        return null;
      }
    } else {
      if (!/^\d{1,3}$/.test(parts[0]) || parts.slice(1).some((part) => part.length !== 3)) return null;
      normalized = parts.join("");
    }
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}


/** Consistent Turkish-lira display formatting for every screen. */
export function formatCurrencyAmount(value: number | null | undefined, fractionDigits = 2): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Turkish thousands/decimal separators without the currency symbol, for inputs. */
export function formatCurrencyInput(value: number): string {
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);
}
